import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type SportType = Database['public']['Enums']['sport_type'];

export interface CourtSchedule {
  id: string;
  court_id: string;
  sub_court_id: string | null;
  day_of_week: number;
  start_time: string;
  end_time: string;
  hourly_price: number;
  is_available: boolean;
  rental_types: string[];
  weekly_discount_percent: number | null;
  monthly_discount_percent: number | null;
  created_at: string;
  updated_at: string;
  court?: {
    id: string;
    name: string;
    location: string;
    sport: SportType;
    image_url: string | null;
    is_verified: boolean | null;
    owner_id: string;
    latitude: number | null;
    longitude: number | null;
    owner?: {
      id: string;
      name: string;
      avatar: string | null;
    };
  };
  sub_court?: {
    id: string;
    name: string;
    sport: SportType;
    hourly_fee: number;
  } | null;
}

const DAYS_OF_WEEK = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

export function getDayLabel(dayOfWeek: number): string {
  return DAYS_OF_WEEK[dayOfWeek] || '';
}

/**
 * Parse PostgreSQL error and return user-friendly message
 */
function parsePostgresError(error: any): string {
  const code = error?.code;
  const message = error?.message || '';
  
  // Custom overlap trigger exception
  if (message.includes('SCHEDULE_OVERLAP')) {
    return 'Khung giờ này bị trùng với lịch đã có trên cùng sân và cùng ngày. Vui lòng chọn thời gian khác.';
  }

  // Unique constraint violation (duplicate schedule)
  if (code === '23505') {
    if (message.includes('idx_court_schedules_unique_slot')) {
      return 'Lịch sân này đã tồn tại. Vui lòng chọn ngày hoặc thời gian khác.';
    }
    return 'Dữ liệu bị trùng lặp. Vui lòng kiểm tra lại.';
  }
  
  // Check constraint violation
  if (code === '23514') {
    if (message.includes('valid_time_range')) {
      return 'Giờ kết thúc phải sau giờ bắt đầu.';
    }
    if (message.includes('valid_hourly_price')) {
      return 'Giá mỗi buổi phải lớn hơn 0.';
    }
    if (message.includes('valid_weekly_discount') || message.includes('valid_monthly_discount')) {
      return 'Giảm giá phải từ 0% đến 100%.';
    }
    return 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.';
  }
  
  // Foreign key violation
  if (code === '23503') {
    return 'Sân không tồn tại hoặc đã bị xóa.';
  }
  
  // Permission denied
  if (code === '42501') {
    return 'Bạn không có quyền thực hiện thao tác này.';
  }
  
  // Connection issues
  if (message.includes('network') || message.includes('connection')) {
    return 'Lỗi kết nối. Vui lòng kiểm tra mạng và thử lại.';
  }
  
  // Default message
  return message || 'Đã xảy ra lỗi. Vui lòng thử lại.';
}

export function useCourtSchedules(filters?: {
  sportFilter?: SportType | 'all';
  locationFilter?: string;
  courtId?: string;
  subCourtId?: string;
}) {
  return useQuery({
    queryKey: ['court-schedules', filters],
    queryFn: async () => {
      let query = supabase
        .from('court_schedules')
        .select(`
          *,
          court:courts!court_schedules_court_id_fkey(
            id, name, location, sport, image_url, is_verified, owner_id, latitude, longitude,
            owner:profiles!courts_owner_id_fkey(id, name, avatar)
          ),
          sub_court:sub_courts!court_schedules_sub_court_id_fkey(
            id, name, sport, hourly_fee
          )
        `)
        .eq('is_available', true)
        .order('day_of_week')
        .order('start_time');

      if (filters?.courtId) {
        query = query.eq('court_id', filters.courtId);
      }

      if (filters?.subCourtId) {
        query = query.eq('sub_court_id', filters.subCourtId);
      }

      const { data, error } = await query;
      if (error) throw error;

      let schedules = data as CourtSchedule[];

      // Filter by sport
      if (filters?.sportFilter && filters.sportFilter !== 'all') {
        schedules = schedules.filter(s => s.court?.sport === filters.sportFilter);
      }

      // Filter by location
      if (filters?.locationFilter) {
        const locationLower = filters.locationFilter.toLowerCase();
        schedules = schedules.filter(s => 
          s.court?.location?.toLowerCase().includes(locationLower)
        );
      }

      return schedules;
    },
  });
}

export function useMyCourtSchedules() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-court-schedules', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data: myCourts, error: courtsError } = await supabase
        .from('courts')
        .select('id')
        .eq('owner_id', profile.id);

      if (courtsError) throw courtsError;
      if (!myCourts || myCourts.length === 0) return [];

      const myCourtIds = myCourts.map(c => c.id);

      const { data, error } = await supabase
        .from('court_schedules')
        .select(`
          *,
          court:courts!court_schedules_court_id_fkey(
            id, name, location, sport, image_url, is_verified, owner_id, latitude, longitude
          ),
          sub_court:sub_courts!court_schedules_sub_court_id_fkey(
            id, name, sport, hourly_fee
          )
        `)
        .in('court_id', myCourtIds)
        .order('day_of_week')
        .order('start_time');

      if (error) throw error;
      return data as CourtSchedule[];
    },
    enabled: !!profile?.id,
  });
}

export function useCreateCourtSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      court_id: string;
      sub_court_id: string;
      day_of_week: number;
      start_time: string;
      end_time: string;
      hourly_price: number;
      rental_types: string[];
      weekly_discount_percent?: number;
      monthly_discount_percent?: number;
    }) => {
      const { data: result, error } = await supabase
        .from('court_schedules')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['my-court-schedules'] });
      toast.success('Đã tạo lịch sân trống');
    },
    onError: (error: any) => {
      const message = parsePostgresError(error);
      toast.error(message);
    },
  });
}

export function useUpdateCourtSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      hourly_price?: number;
      is_available?: boolean;
      rental_types?: string[];
      weekly_discount_percent?: number;
      monthly_discount_percent?: number;
    }) => {
      const { id, ...updates } = data;
      const { data: result, error } = await supabase
        .from('court_schedules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['my-court-schedules'] });
      toast.success('Đã cập nhật lịch sân');
    },
    onError: (error: any) => {
      const message = parsePostgresError(error);
      toast.error(message);
    },
  });
}

export function useDeleteCourtSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('court_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['my-court-schedules'] });
      toast.success('Đã xóa lịch sân');
    },
    onError: (error: any) => {
      const message = parsePostgresError(error);
      toast.error(message);
    },
  });
}
